import { productEvent } from "@shared/analytics/productEvents";
import type { ProductJourney } from "@shared/analytics/productEvents";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import { Button } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";

import { EarnContent } from "@/pages/USDv/components/SwapStake/EarnContent";

import type { SharedDestination } from "../state/types";

/**
 * Full PanelMenu + Rewards Routing percentage table + Destination
 * Accounts list. Reached via the "Configure rewards routing" link in
 * the footer. Wraps `EarnContent` so the destinations list lifted to
 * the page can flow through. (`EarnContent` no longer has a mode to
 * pick — it is settings-only now that this is its one consumer.)
 */
export function ConfigureRoutingPanel({
    analytics,
    destinations,
    onDestinationsChange,
    connectedWallet,
    onCreateDestination,
    isCreatingDestination,
    onBack
}: {
    analytics?: ProductJourney;
    destinations: SharedDestination[];
    onDestinationsChange: React.Dispatch<
        React.SetStateAction<SharedDestination[]>
    >;
    connectedWallet: string;
    onCreateDestination?: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<
        | { success: true; destination: SharedDestination }
        | { success: false; error: string }
    >;
    isCreatingDestination?: boolean;
    onBack: () => void;
}): React.ReactElement {
    return (
        <motion.div
            key="configure-routing"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
            className="flex flex-col gap-3"
        >
            <Button
                variant="link"
                size="sm"
                onClick={() => {
                    if (analytics) analytics.close("back");
                    else
                        productEvent("saved_destination:form_closed", {
                            entry_point: "rewards_footer",
                            close_method: "back"
                        });
                    onBack();
                }}
                className="self-start"
            >
                <ArrowShortLeftIcon className="size-4" />
                Back
            </Button>
            <EarnContent
                analytics={analytics}
                showPanelTabs
                destinations={destinations}
                onDestinationsChange={onDestinationsChange}
                connectedWallet={connectedWallet}
                onCreateDestination={onCreateDestination}
                isCreatingDestination={isCreatingDestination}
            />
        </motion.div>
    );
}
